import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { LoadingState } from "@/components/ui/loading-state";
import { logger } from "@/lib/logger";

export const Route = createFileRoute("/_authenticated")({
  // Gate the entire authenticated subtree before any child route renders.
  // Runs on both server and client; on the client it awaits the restored
  // Supabase session so we never flash protected UI to a signed-out user.
  beforeLoad: async ({ location }) => {
    // SSR has no session storage — defer the check to the client render.
    if (typeof window === "undefined") return;
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      logger.error("Protected-route session restore failed", error, { path: location.href });
    }
    if (!data.session) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  // Auth context still hydrates roles/customer access; show a brief spinner
  // only while that secondary data loads. The session itself is already
  // guaranteed by beforeLoad.
  const { loading, user } = useAuth();
  if (loading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingState label="Loading workspace…" />
      </div>
    );
  }
  return <Outlet />;
}